import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import BilingualInput from '../../components/admin/BilingualInput';
import BilingualTextArea from '../../components/admin/BilingualTextArea';
import ClientsEditorFields from '../../components/admin/ClientsEditorFields';
import ClientLogoUploadGrid from '../../components/admin/ClientLogoUploadGrid';
import ServiceCardManager from '../../components/admin/ServiceCardManager';
import {
    Home, Type, PanelBottom, Mail, HardHat, DollarSign, Zap, Mountain, Building2,
    LogOut, Save, Image as ImageIcon, CheckCircle2, AlertCircle, Loader2,
    Layout, FileText, RefreshCw, MapPin, Star, Target, Phone, Info, Briefcase, Plane, Truck, Users, Wrench
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const branchDefaults = [
    { name: 'Muscat (Ghala) HQ', phone: '+(968) 24-595058', fax: '+(968) 24-501818', email: 'Ghala.Dunlop@hamdanoman.com', coords: '23.575480, 58.354941' },
    { name: 'Salalah (New Salalah)', phone: '+(968) 23-291561', fax: '+(968) 232-97035', email: 'Salalah.Dunlop@hamdanoman.com', coords: '17.021328, 54.117397' },
    { name: 'Ibri', phone: '+(968) 25-691242', fax: '+(968) 25-691242', email: 'Ibri.Dunlop@hamdanoman.com', coords: '23.215939, 56.512076' },
    { name: 'Rustaq', phone: '+(968) 9910825', fax: '', email: 'Rustaq.Dunlop@hamdanoman.com', coords: '23.434510, 57.391902' },
    { name: 'Sinaw', phone: '+(968) 23-436134', fax: '+(968) 23-436134', email: 'Sinaw.Dunlop@hamdanoman.com', coords: '22.505329, 58.034167' },
    { name: 'Sohar', phone: '+(968) 26-846433', fax: '+(968) 26-846866', email: 'Sohar.Dunlop@hamdanoman.com', coords: '24.343390, 56.697076' },
    { name: 'Muscat (Al Wadi Al Kabir)', phone: '+(968) 24-811458', fax: '+(968) 24-814380', email: 'Wadikabir.Dunlop@hamdanoman.com', coords: '23.587644, 58.554047' },
    { name: 'Barka', phone: '+(968) 26-884250', fax: '+(968) 26-884235', email: 'Barka.Dunlop@hamdanoman.com', coords: '23.667609, 57.889009' },
    { name: 'Suwayq', phone: '+(968) 99108262', fax: '', email: 'Suwayq.Dunlop@hamdanoman.com', coords: '23.902097, 57.220016' },
    { name: 'Haima', phone: '+(968) 23-436134', fax: '+(968) 23-436134', email: 'Haima.Dunlop@hamdanoman.com', coords: '19.950266, 56.264675' },
    { name: 'Buraimi', phone: '+(968) 25-651510', fax: '+(968) 25-651510', email: 'Buraimi.Dunlop@hamdanoman.com', coords: '24.255326, 55.769307' },
    { name: 'Sur', phone: '+(968) 25-541017', fax: '+(968) 25-541017', email: 'Sur.Dunlop@hamdanoman.com', coords: '22.568787, 59.497667' },
];

const AdminTyres: React.FC = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'content' | 'images'>('content');
    const [activeSection, setActiveSection] = useState<'banner' | 'about' | 'services' | 'promotions' | 'branches' | 'clients' | 'contact'>('banner');
    const [content, setContent] = useState<any>({});
    const [saving, setSaving] = useState<string | null>(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [username, setUsername] = useState('');
    const [pendingImages, setPendingImages] = useState<Record<string, { file: File, preview: string }>>({});
    const [activeTyresBranches, setActiveTyresBranches] = useState<number[]>([]);

    useEffect(() => {
        const user = localStorage.getItem('adminUser');
        const token = localStorage.getItem('adminToken');
        if (!token) { navigate('/admin/login'); return; }
        setUsername(user || 'Admin');
        fetchContent();
    }, [navigate]);

    const fetchContent = async () => {
        try {
            const res = await fetch('/api/homepage/content');
            const data = await res.json();
            
            if (data.Tyres) {
                const hasBranches = Object.keys(data.Tyres).some(k => k.startsWith('branch_') && k.endsWith('_name') && data.Tyres[k]);
                if (!hasBranches) {
                    branchDefaults.forEach((d, idx) => {
                        const n = idx + 1;
                        data.Tyres[`branch_${n}_name`] = d.name;
                        data.Tyres[`branch_${n}_phone`] = d.phone;
                        data.Tyres[`branch_${n}_fax`] = d.fax;
                        data.Tyres[`branch_${n}_email`] = d.email;
                        data.Tyres[`branch_${n}_coords`] = d.coords;
                    });
                }
                const ids = [];
                for (let n = 1; n <= 30; n++) {
                    if (data.Tyres[`branch_${n}_name`]) {
                        ids.push(n);
                    }
                }
                setActiveTyresBranches(ids);
            }
            setContent(data);
        } catch (err) { console.error('Failed to fetch content', err); }
    };

    const handleLogout = () => {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        navigate('/admin/login');
    };

    const updateField = (key: string, value: string) => {
        setContent((prev: any) => ({
            ...prev,
            Tyres: { ...prev.Tyres, [key]: value }
        }));
    };

    const saveContent = async () => {
        setSaving('Tyres');
        setMessage({ text: 'Saving content...', type: '' });
        try {
            const updates = Object.entries(content.Tyres || {}).map(([key, value]) => {
                // Skip Arabic keys in the main loop as we'll send them alongside the English key
                if (key.endsWith('_ar')) return null;

                return fetch('/api/homepage/update-text', {
                    method: 'POST',
                    headers: { 
                        'Content-Type': 'application/json', 
                        'Authorization': `Bearer ${localStorage.getItem('adminToken')}` 
                    },
                    body: JSON.stringify({ 
                        section: 'Tyres', 
                        content_key: key, 
                        content_value: value,
                        content_value_ar: content.Tyres[`${key}_ar`] || null
                    })
                });
            }).filter(p => p !== null);
            await Promise.all(updates);
            setMessage({ text: 'Content updated successfully!', type: 'success' });
            fetchContent();
        } catch {
            setMessage({ text: 'Failed to update content', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 4000);
        }
    };

    const handleImageSelect = (key: string, file: File) => {
        const preview = URL.createObjectURL(file);
        setPendingImages(prev => ({ ...prev, [key]: { file, preview } }));
    };

    const saveImages = async () => {
        setSaving('images');
        setMessage({ text: 'Uploading images...', type: '' });
        try {
            const promises = Object.entries(pendingImages).map(async ([key, pending]) => {
                const formData = new FormData();
                formData.append('section', 'Tyres');
                formData.append('content_key', key);
                formData.append('image', pending.file);
                const res = await fetch('/api/homepage/update-image', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
                    body: formData
                });
                if (!res.ok) console.error(`Upload failed for ${key}`);
                return res;
            });
            const results = await Promise.all(promises);
            if (results.every(r => r.ok)) {
                setMessage({ text: 'All images uploaded successfully!', type: 'success' });
                setPendingImages({});
                fetchContent();
            } else {
                setMessage({ text: 'Some uploads failed. Check console.', type: 'error' });
            }
        } catch {
            setMessage({ text: 'Connectivity error. Check server.', type: 'error' });
        } finally {
            setSaving(null);
            setTimeout(() => setMessage({ text: '', type: '' }), 4000);
        }
    };

    const sections = [
        { id: 'banner', label: 'Hero Banner', icon: Layout },
        { id: 'about', label: 'About Us', icon: FileText },
        { id: 'services', label: 'Services', icon: Wrench },
        { id: 'promotions', label: 'Promotions', icon: Target },
        { id: 'branches', label: 'Branches', icon: MapPin },
        { id: 'clients', label: 'Clients', icon: Users },
        { id: 'contact', label: 'Contact', icon: Mail },
    ];

    const UploadBox = ({ keyName, label, currentImage }: any) => {
        const pending = pendingImages[keyName];
        const displayImage = pending ? pending.preview : currentImage;
        return (
            <div className="group bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:border-blue-200 transition-all flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{label}</span>
                    {currentImage && !pending && (
                        <div className="flex items-center gap-1 text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-lg text-[8px] font-bold uppercase">
                            <CheckCircle2 className="w-3 h-3" /> Live
                        </div>
                    )}
                </div>
                <div className="relative aspect-video bg-slate-50 rounded-xl overflow-hidden mb-4 border border-slate-100">
                    {displayImage ? (
                        <img src={displayImage} className="w-full h-full object-cover" alt="Preview" />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                            <ImageIcon className="w-8 h-8 opacity-20" />
                        </div>
                    )}
                    <label className="absolute inset-0 cursor-pointer bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-white" />
                        <input type="file" className="hidden" accept="image/*" onChange={e => {
                            const file = e.target.files?.[0];
                            if (file) handleImageSelect(keyName, file);
                        }} />
                    </label>
                </div>
                {pending && <div className="text-[8px] text-amber-500 font-bold uppercase text-center">Unsaved Changes</div>}
            </div>
        );
    };

    const inputCls = "w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-slate-800";
    const textCls = "w-full p-4 bg-slate-50 border border-slate-100 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none font-medium leading-relaxed text-slate-700";
    const smallInputCls = "bg-white border border-slate-200 p-3 rounded-xl text-xs font-bold text-slate-800 w-full";
    const labelCls = "text-[10px] font-bold text-slate-400 uppercase tracking-widest";
    const smallLabelCls = "text-[8px] font-black text-slate-400 uppercase";

    const T = content.Tyres || {};

    return (
        <div className="flex h-screen bg-slate-50 font-sans overflow-hidden">
            <AdminSidebar />

            {/* Main */}
            <div className="flex-1 flex flex-col h-full overflow-hidden">
                <header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-10 shadow-sm shrink-0 z-30">
                    <div className="flex items-center space-x-6">
                        <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                            <Target className="w-6 h-6 text-blue-600" /> Tyres & Services Editor
                        </h2>
                        <div className="flex bg-slate-100 p-1.5 rounded-2xl">
                            <button onClick={() => setActiveTab('content')} className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'content' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>
                                <Type className="w-4 h-4" /><span>Content Details</span>
                            </button>
                            <button onClick={() => setActiveTab('images')} className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl font-bold text-sm transition-all ${activeTab === 'images' ? 'bg-white text-blue-600 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>
                                <ImageIcon className="w-4 h-4" /><span>Media Assets</span>
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center space-x-6">
                        <div className="hidden sm:block text-right">
                            <div className="text-sm font-bold text-slate-900">{username}</div>
                            <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Portal Manager</div>
                        </div>
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center border border-slate-200">
                            <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        </div>
                    </div>
                </header>

                <main className="flex-1 overflow-y-auto p-10">
                    <div className="max-w-6xl mx-auto">
                        <AnimatePresence mode="wait">
                            {activeTab === 'content' ? (
                                <motion.div key="content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="space-y-8">
                                    {/* Section Nav */}
                                    <div className="flex flex-wrap gap-3 mb-8">
                                        {sections.map(sec => (
                                            <button key={sec.id} onClick={() => setActiveSection(sec.id as any)}
                                                className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl font-bold text-xs transition-all border ${activeSection === sec.id ? 'bg-blue-600 border-blue-600 text-white shadow-lg' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                                                <sec.icon className="w-4 h-4" /><span>{sec.label}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}

                                    <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm">
                                        <div className="flex items-center justify-between mb-8">
                                            <h3 className="text-lg font-bold text-slate-800 flex items-center capitalize">
                                                <div className="w-1.5 h-6 bg-blue-600 rounded-full mr-3" />
                                                {activeSection} Configuration
                                            </h3>
                                            <button onClick={saveContent} disabled={!!saving}
                                                className="flex items-center space-x-2 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 text-white rounded-xl font-bold text-sm transition-all shadow-lg active:scale-95">
                                                {saving === 'Tyres' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                                <span>Save {activeSection}</span>
                                            </button>
                                        </div>

                                        {/* ── BANNER ── */}
                                        {activeSection === 'banner' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="Banner Title"
                                                    englishValue={T.banner_title ?? 'Hamdan Tires , Battery & Accessories'}
                                                    arabicValue={T.banner_title_ar}
                                                    onChangeEnglish={val => updateField('banner_title', val)}
                                                    onChangeArabic={val => updateField('banner_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="Banner Subtitle"
                                                    englishValue={T.banner_subtitle ?? 'The registered sole distributer of Dunlop Tyres & Tubes in Oman Since 1981'}
                                                    arabicValue={T.banner_subtitle_ar}
                                                    onChangeEnglish={val => updateField('banner_subtitle', val)}
                                                    onChangeArabic={val => updateField('banner_subtitle_ar', val)}
                                                    rows={2}
                                                />
                                            </div>
                                        )}

                                        {/* ── ABOUT US ── */}
                                        {activeSection === 'about' && (
                                            <div className="space-y-6">
                                                <BilingualInput 
                                                    label="Section Title"
                                                    englishValue={T.about_title ?? 'Hamdan Tyres & Services'}
                                                    arabicValue={T.about_title_ar}
                                                    onChangeEnglish={val => updateField('about_title', val)}
                                                    onChangeArabic={val => updateField('about_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="Description"
                                                    englishValue={T.about_desc ?? 'Over the years with increased quality of roads...'}
                                                    arabicValue={T.about_desc_ar}
                                                    onChangeEnglish={val => updateField('about_desc', val)}
                                                    onChangeArabic={val => updateField('about_desc_ar', val)}
                                                    rows={6}
                                                />
                                                <div className="pt-4 border-t border-slate-100">
                                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">3 Key Feature Points</h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                        {[
                                                            { i: 1, def_t: 'Global Import', def_d: 'Tyres from 8 countries worldwide' },
                                                            { i: 2, def_t: 'Since 1981', def_d: '40+ years of trusted service' },
                                                            { i: 3, def_t: 'Wide Coverage', def_d: '12 branches across Oman' },
                                                        ].map(({ i, def_t, def_d }) => (
                                                            <div key={i} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-4">
                                                                <span className="text-[10px] font-black text-blue-600 uppercase">Feature {i}</span>
                                                                <BilingualInput 
                                                                    label="Title"
                                                                    englishValue={T[`about_pt_${i}_title`] ?? def_t}
                                                                    arabicValue={T[`about_pt_${i}_title_ar`]}
                                                                    onChangeEnglish={val => updateField(`about_pt_${i}_title`, val)}
                                                                    onChangeArabic={val => updateField(`about_pt_${i}_title_ar`, val)}
                                                                />
                                                                <BilingualTextArea 
                                                                    label="Description"
                                                                    englishValue={T[`about_pt_${i}_desc`] ?? def_d}
                                                                    arabicValue={T[`about_pt_${i}_desc_ar`]}
                                                                    onChangeEnglish={val => updateField(`about_pt_${i}_desc`, val)}
                                                                    onChangeArabic={val => updateField(`about_pt_${i}_desc_ar`, val)}
                                                                    rows={2}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* ── SERVICES ── */}
                                        {activeSection === 'services' && (
                                            <ServiceCardManager
                                                section="Tyres"
                                                data={T}
                                                onSaved={fetchContent}
                                                sectionTitle={T.services_title ?? 'Our Services'}
                                                sectionTitleAr={T.services_title_ar}
                                                onChangeSectionTitle={val => updateField('services_title', val)}
                                                onChangeSectionTitleAr={val => updateField('services_title_ar', val)}
                                            />
                                        )}

                                        {/* ── PROMOTIONS ── */}
                                        {activeSection === 'promotions' && (
                                            <div className="space-y-6">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                    <BilingualInput 
                                                        label="Date / Number (on badge)"
                                                        englishValue={T.promo_date ?? '2'}
                                                        arabicValue={T.promo_date_ar}
                                                        onChangeEnglish={val => updateField('promo_date', val)}
                                                        onChangeArabic={val => updateField('promo_date_ar', val)}
                                                    />
                                                    <BilingualInput 
                                                        label="Month & Year (on badge)"
                                                        englishValue={T.promo_year ?? 'Febr 2014'}
                                                        arabicValue={T.promo_year_ar}
                                                        onChangeEnglish={val => updateField('promo_year', val)}
                                                        onChangeArabic={val => updateField('promo_year_ar', val)}
                                                    />
                                                </div>
                                                <BilingualInput 
                                                    label="Promotion Title"
                                                    englishValue={T.promo_title ?? 'Dunlop Sales Promotion During April to June 2015'}
                                                    arabicValue={T.promo_title_ar}
                                                    onChangeEnglish={val => updateField('promo_title', val)}
                                                    onChangeArabic={val => updateField('promo_title_ar', val)}
                                                />
                                                <BilingualTextArea 
                                                    label="Promotion Subtitle / Description"
                                                    englishValue={T.promo_subtitle ?? 'Sales Promotion During April to June 2015 for our Dealers and cash customers all stores of Oman.'}
                                                    arabicValue={T.promo_subtitle_ar}
                                                    onChangeEnglish={val => updateField('promo_subtitle', val)}
                                                    onChangeArabic={val => updateField('promo_subtitle_ar', val)}
                                                    rows={3}
                                                />
                                                <div className="pt-4 border-t border-slate-100">
                                                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">3 Feature Points (below images)</h4>
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                        {[
                                                            { i: 1, def_t: 'Prize Campaign', def_d: 'Scratch & Win prizes up to 50 OMR' },
                                                            { i: 2, def_t: 'Seasonal Offers', def_d: 'Valid April to June 2015' },
                                                            { i: 3, def_t: 'Quality Rewards', def_d: 'Rewarding premium Dunlop tyre purchases' },
                                                        ].map(({ i, def_t, def_d }) => (
                                                            <div key={i} className="p-4 bg-yellow-50 rounded-2xl border border-yellow-100 space-y-4">
                                                                <span className="text-[10px] font-black text-yellow-700 uppercase">Point {i}</span>
                                                                <BilingualInput 
                                                                    label="Title"
                                                                    englishValue={T[`promo_pt_${i}_title`] ?? def_t}
                                                                    arabicValue={T[`promo_pt_${i}_title_ar`]}
                                                                    onChangeEnglish={val => updateField(`promo_pt_${i}_title`, val)}
                                                                    onChangeArabic={val => updateField(`promo_pt_${i}_title_ar`, val)}
                                                                />
                                                                <BilingualTextArea 
                                                                    label="Description"
                                                                    englishValue={T[`promo_pt_${i}_desc`] ?? def_d}
                                                                    arabicValue={T[`promo_pt_${i}_desc_ar`]}
                                                                    onChangeEnglish={val => updateField(`promo_pt_${i}_desc`, val)}
                                                                    onChangeArabic={val => updateField(`promo_pt_${i}_desc_ar`, val)}
                                                                    rows={2}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* ── BRANCHES (CRUD) ── */}
                                        {activeSection === 'branches' && (() => {
                                             // Build current branches from content
                                             const getBranches = () => {
                                                 return activeTyresBranches.map(n => ({
                                                     n,
                                                     name: T[`branch_${n}_name`] || '',
                                                     phone: T[`branch_${n}_phone`] || '',
                                                     fax: T[`branch_${n}_fax`] || '',
                                                     email: T[`branch_${n}_email`] || '',
                                                     coords: T[`branch_${n}_coords`] || '',
                                                 }));
                                             };

                                             const branches = getBranches();
                                             const nextN = activeTyresBranches.length > 0 ? Math.max(...activeTyresBranches) + 1 : 1;

                                             const handleDelete = (n: number) => {
                                                 if (!window.confirm('Delete this branch?')) return;
                                                 // Clear all fields for this branch slot
                                                 ['name', 'phone', 'fax', 'email', 'coords'].forEach(field => {
                                                     updateField(`branch_${n}_${field}`, '');
                                                 });
                                                 setActiveTyresBranches(prev => prev.filter(id => id !== n));
                                             };

                                             const handleAdd = () => {
                                                 updateField(`branch_${nextN}_name`, 'New Branch');
                                                 updateField(`branch_${nextN}_phone`, '');
                                                 updateField(`branch_${nextN}_fax`, '');
                                                 updateField(`branch_${nextN}_email`, '');
                                                 updateField(`branch_${nextN}_coords`, '');
                                                 setActiveTyresBranches(prev => [...prev, nextN]);
                                             };

                                            return (
                                                <div className="space-y-4">
                                                    <div className="flex items-center justify-between">
                                                        <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex-1 mr-4">
                                                            <p className="text-xs font-bold text-blue-700">
                                                                {branches.length} branch(es) — Edit inline, then click "Save Branches" above.
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={handleAdd}
                                                            className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-sm transition-all shadow-lg"
                                                        >
                                                            <span className="text-lg leading-none">+</span> Add Branch
                                                        </button>
                                                    </div>

                                                    {branches.length === 0 && (
                                                        <div className="text-center py-12 text-slate-400">
                                                            <p className="text-sm font-bold">No branches yet.</p>
                                                            <p className="text-xs mt-1">Click "Add Branch" to create the first one.</p>
                                                        </div>
                                                    )}

                                                    <div className="grid grid-cols-1 gap-6">
                                                        {branches.map((branch) => (
                                                            <div key={branch.n} className="p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-6">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="text-[10px] font-black text-blue-600 uppercase">Branch #{branch.n}</span>
                                                                    <button
                                                                        onClick={() => handleDelete(branch.n)}
                                                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-bold transition-all"
                                                                    >
                                                                        🗑 Delete
                                                                    </button>
                                                                </div>
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                                    <BilingualInput 
                                                                        label="Branch Name"
                                                                        englishValue={T[`branch_${branch.n}_name`] ?? branch.name}
                                                                        arabicValue={T[`branch_${branch.n}_name_ar`]}
                                                                        onChangeEnglish={val => updateField(`branch_${branch.n}_name`, val)}
                                                                        onChangeArabic={val => updateField(`branch_${branch.n}_name_ar`, val)}
                                                                    />
                                                                    <BilingualInput 
                                                                        label="Phone"
                                                                        englishValue={T[`branch_${branch.n}_phone`] ?? branch.phone}
                                                                        arabicValue={T[`branch_${branch.n}_phone_ar`]}
                                                                        onChangeEnglish={val => updateField(`branch_${branch.n}_phone`, val)}
                                                                        onChangeArabic={val => updateField(`branch_${branch.n}_phone_ar`, val)}
                                                                    />
                                                                    <BilingualInput 
                                                                        label="Fax"
                                                                        englishValue={T[`branch_${branch.n}_fax`] ?? branch.fax}
                                                                        arabicValue={T[`branch_${branch.n}_fax_ar`]}
                                                                        onChangeEnglish={val => updateField(`branch_${branch.n}_fax`, val)}
                                                                        onChangeArabic={val => updateField(`branch_${branch.n}_fax_ar`, val)}
                                                                    />
                                                                    <BilingualInput 
                                                                        label="Email"
                                                                        englishValue={T[`branch_${branch.n}_email`] ?? branch.email}
                                                                        arabicValue={T[`branch_${branch.n}_email_ar`]}
                                                                        onChangeEnglish={val => updateField(`branch_${branch.n}_email`, val)}
                                                                        onChangeArabic={val => updateField(`branch_${branch.n}_email_ar`, val)}
                                                                    />
                                                                    <div className="md:col-span-2">
                                                                        <BilingualInput 
                                                                            label="Coordinates (lat, lng)"
                                                                            englishValue={T[`branch_${branch.n}_coords`] ?? branch.coords}
                                                                            arabicValue={T[`branch_${branch.n}_coords_ar`]}
                                                                            onChangeEnglish={val => updateField(`branch_${branch.n}_coords`, val)}
                                                                            onChangeArabic={val => updateField(`branch_${branch.n}_coords_ar`, val)}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Network Summary */}
                                                    <div className="pt-4 border-t-2 border-blue-100">
                                                        <div className="flex items-center gap-2 mb-4">
                                                            <Info className="w-4 h-4 text-blue-600" />
                                                            <h4 className="text-sm font-black text-blue-700 uppercase tracking-widest">Network Summary Section</h4>
                                                        </div>
                                                        <div className="space-y-8">
                                                            <BilingualInput 
                                                                label="Summary Title"
                                                                englishValue={T.network_title ?? 'Our Network Coverage'}
                                                                arabicValue={T.network_title_ar}
                                                                onChangeEnglish={val => updateField('network_title', val)}
                                                                onChangeArabic={val => updateField('network_title_ar', val)}
                                                            />
                                                            <BilingualTextArea 
                                                                label="Summary Description"
                                                                englishValue={T.network_desc ?? 'With branches strategically located across the Sultanate of Oman, we ensure comprehensive coverage.'}
                                                                arabicValue={T.network_desc_ar}
                                                                onChangeEnglish={val => updateField('network_desc', val)}
                                                                onChangeArabic={val => updateField('network_desc_ar', val)}
                                                                rows={3}
                                                            />
                                                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">4 Network Stats</h4>
                                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                                {[
                                                                    { i: 1, def_v: '12', def_l: 'Total Branches' },
                                                                    { i: 2, def_v: '3', def_l: 'Major Cities' },
                                                                    { i: 3, def_v: '9', def_l: 'Regional Areas' },
                                                                    { i: 4, def_v: '40+', def_l: 'Years Experience' },
                                                                ].map(({ i, def_v, def_l }) => (
                                                                    <div key={i} className="flex flex-col gap-4 p-4 bg-white rounded-2xl border border-slate-100">
                                                                        <BilingualInput 
                                                                            label={`Stat ${i} Value`}
                                                                            englishValue={T[`network_stat_${i}_value`] ?? def_v}
                                                                            arabicValue={T[`network_stat_${i}_value_ar`]}
                                                                            onChangeEnglish={val => updateField(`network_stat_${i}_value`, val)}
                                                                            onChangeArabic={val => updateField(`network_stat_${i}_value_ar`, val)}
                                                                        />
                                                                        <BilingualInput 
                                                                            label={`Stat ${i} Label`}
                                                                            englishValue={T[`network_stat_${i}_label`] ?? def_l}
                                                                            arabicValue={T[`network_stat_${i}_label_ar`]}
                                                                            onChangeEnglish={val => updateField(`network_stat_${i}_label`, val)}
                                                                            onChangeArabic={val => updateField(`network_stat_${i}_label_ar`, val)}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Clients */}
                                        {activeSection === 'clients' && (
                                            <ClientsEditorFields
                                                data={T}
                                                updateField={updateField}
                                                defaultDescription="Dealer, cash customer, and corporate clients served by Hamdan Tyres."
                                            />
                                        )}

                                        {/* ── CONTACT ── */}
                                        {activeSection === 'contact' && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                                {/* HQ */}
                                                <div className="space-y-8 p-6 bg-slate-50 rounded-3xl border border-slate-100">
                                                    <h4 className="text-xs font-black text-blue-600 uppercase tracking-widest mb-4">Muscat Ghala HQ</h4>
                                                    {[
                                                        { id: 'hq_address', l: 'Address', def: 'Musqat Ghala HQ' },
                                                        { id: 'hq_mobile', l: 'Mobile', def: '+968 95770994' },
                                                        { id: 'hq_phone', l: 'Telephone', def: '+968 24595058' },
                                                        { id: 'hq_fax', l: 'Fax', def: '+968 24501818' },
                                                        { id: 'hq_email', l: 'Email', def: 'Alwyn.Martin@hamdanoman.com' },
                                                    ].map(f => (
                                                        <BilingualInput 
                                                            key={f.id}
                                                            label={f.l}
                                                            englishValue={T[f.id] ?? f.def}
                                                            arabicValue={T[`${f.id}_ar`]}
                                                            onChangeEnglish={val => updateField(f.id, val)}
                                                            onChangeArabic={val => updateField(`${f.id}_ar`, val)}
                                                        />
                                                    ))}
                                                </div>
                                                {/* Wadi Kabir */}
                                                <div className="space-y-8 p-6 bg-slate-50 rounded-3xl border border-slate-100 h-fit">
                                                    <h4 className="text-xs font-black text-blue-600 uppercase tracking-widest mb-4">Muscat Al Wadi Al Kabir</h4>
                                                    {[
                                                        { id: 'wadi_phone', l: 'Telephone', def: '+968 24-811458' },
                                                        { id: 'wadi_fax', l: 'Fax', def: '+968 24-814380' },
                                                        { id: 'wadi_email', l: 'Email', def: 'wadikabir.dunlop@hamdanoman.com' },
                                                    ].map(f => (
                                                        <BilingualInput 
                                                            key={f.id}
                                                            label={f.l}
                                                            englishValue={T[f.id] ?? f.def}
                                                            arabicValue={T[`${f.id}_ar`]}
                                                            onChangeEnglish={val => updateField(f.id, val)}
                                                            onChangeArabic={val => updateField(`${f.id}_ar`, val)}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.div key="images" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="space-y-8">
                                    {message.text && (
                                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className={`p-4 rounded-2xl font-bold text-sm flex items-center gap-3 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                                            {message.type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
                                            {message.text}
                                        </motion.div>
                                    )}
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex-1 mr-6">
                                            <h3 className="text-xl font-black text-slate-800">Media Management</h3>
                                            <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">All Tyres & Services Images</p>
                                        </div>
                                        <button onClick={saveImages} disabled={Object.keys(pendingImages).length === 0 || !!saving}
                                            className="px-10 py-5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-3xl font-black text-xs uppercase tracking-widest transition-all shadow-2xl flex items-center space-x-3">
                                            {saving === 'images' ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
                                            <span>Synchronize {Object.keys(pendingImages).length} Assets</span>
                                        </button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        {/* Banner Images */}
                                        <UploadBox keyName="banner_1" label="Hero Banner 1" currentImage={T.banner_1 ?? '/banner/hamdan-tyres-1.jpg'} />
                                        <UploadBox keyName="banner_2" label="Hero Banner 2" currentImage={T.banner_2 ?? '/banner/hamdan-tyres-2.jpg'} />
                                        <UploadBox keyName="banner_3" label="Hero Banner 3" currentImage={T.banner_3 ?? '/banner/hamdan-tyres-3.jpg'} />

                                        {/* About */}
                                        <UploadBox keyName="about_image" label="About Logo / Image" currentImage={T.about_image ?? '/Hamdan-Dunlop-logo.jpg'} />

                                        {/* Promotions */}
                                        <UploadBox keyName="promo_image_1" label="Promotion Poster (English)" currentImage={T.promo_image_1 ?? '/Scratch-English_1.jpg'} />
                                        <UploadBox keyName="promo_image_2" label="Promotion Poster (Arabic)" currentImage={T.promo_image_2 ?? '/Scratch-Arabic_1.jpg'} />

                                        {/* Service Images */}
                                        <UploadBox keyName="svc_1_image" label="Service 1 Image" currentImage={T.svc_1_image} />
                                        <UploadBox keyName="svc_2_image" label="Service 2 Image" currentImage={T.svc_2_image} />
                                        <UploadBox keyName="svc_3_image" label="Service 3 Image" currentImage={T.svc_3_image} />
                                    </div>

                                    <div className="mt-12">
                                        <ClientLogoUploadGrid
                                            data={T}
                                            pendingImages={pendingImages}
                                            onSelect={handleImageSelect}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminTyres;
